import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { CostSettingProfile } from 'lumen/models';

export interface SettingsCostTablesCellsTdTypesSignature {
  Args: {
    cellData: CostSettingProfile;
  };
  Element: HTMLTableCellElement;
}

export default class SettingsCostTablesCellsTdTypes extends Component<SettingsCostTablesCellsTdTypesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarket: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get currencySymbol(): string {
    const { currencyCode } = this.hotelAndMarket.hotelOrMarket;

    return CURRENCY_MAPPING[currencyCode] ?? currencyCode;
  }

  get pills(): string[] {
    // Simple: "<type>: <value>%" / "<type>: <symbol><value>". Advanced (a
    // per-weekday matrix) can't be a single number, so it shows "Advanced".
    const label = this.intl.t('settings.cost.commission');

    return this.args.cellData.costs.reduce<string[]>((labels, cost) => {
      if (cost.commissionType === 'advanced') {
        labels.push(`${label}: ${this.intl.t('settings.cost.mode.advanced')}`);
        return labels;
      }

      const { value } = cost.costValues;
      if (value !== null) {
        const formatted =
          cost.valueType === 'currency'
            ? `${this.currencySymbol}${value}`
            : `${value}%`;
        labels.push(`${label}: ${formatted}`);
      }
      return labels;
    }, []);
  }

  <template>
    <div class="flex flex-wrap gap-100 py-200">
      {{#each this.pills as |pill|}}
        <PrismBadge
          @label={{pill}}
          @type="neutral"
          data-test-id="cost-type-pill"
        />
      {{else}}
        <span data-test-id="cost-type-missing">{{t "general.missing"}}</span>
      {{/each}}
    </div>
  </template>
}
